import { useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Container, TextInput, Select } from '@mantine/core';
import { useForm } from "@mantine/form";
import { Row, Col } from 'react-bootstrap';
import PhoneInputGroup from '../components/PhoneInputGroup';
import { SaveResponses } from "../actions/ResponsesActions";
import { useDebouncedValue } from "@mantine/hooks";
import CellPhoneCountrys from '../enums/CellPhoneCountrys';
import { GetConocio, GetPaises, GetEligio, GetProvincias } from "../actions/FormDataActions";
import useFormTracking from "../hooks/useFormTracking";
const ContactDataForm = forwardRef(({ stepNumber, id,queryparams }, ref) => {

  const dispatch = useDispatch();
  const response = useSelector((state) => state.respuestas.steps[stepNumber]?.responses || {});
  const {data: dataConocio} = useSelector((state) => state.datosFormulario.conocio);
  const {data: dataPaises} = useSelector((state) => state.datosFormulario.paises);
  const {data: dataProvincias} = useSelector((state) => state.datosFormulario.provincias);
  const {data: dataEligio} = useSelector((state) => state.datosFormulario.eligio);

  const { track } = useFormTracking(stepNumber);
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };

  const trackOnBlur = (fieldName) => () => {
    if (form.values[fieldName]) track('field_completed', { fieldName });
  };

  const form = useForm({
    initialValues: {
      calle: response.calle || '',
      altura: response.altura || '',
      piso: response.piso || '',
      departamento: response.departamento || '',
      codPostal: response.codPostal || '',
      localidad: response.localidad || '',
      paisResidencia: response.paisResidencia || 'AR',
      provincia: response.provincia || 'BAI',
      provinciaDescripcion: response.provinciaDescripcion || '',
      codAreaParticular: response.codAreaParticular || '+54',
      telefonoParticular: response.telefonoParticular || '',
      conocioInstitucion: response.conocioInstitucion || null,
      eligioInstitucion: response.eligioInstitucion || null,
    },
    validate: {
      calle: (value) => (value?.length > 2 ? null : "El campo es obligatorio"),
      altura: (value) => (value?.length > 0 ? null : "El campo es obligatorio"),
      codPostal: (value) => (value?.length > 2 ? null : "El campo es obligatorio"),
      localidad: (value) => (value?.length > 2 ? null : "El campo es obligatorio"),
      paisResidencia: (value) => (value ? null : "El campo es obligatorio"),
      provincia: (value) => (value ? null : "El campo es obligatorio"),
      eligioInstitucion: (value) => (value ? null : "El campo es obligatorio"),
      // Se mostraba como obligatorio (asterisco) pero no se validaba: vacío, NBAPI lo mandaba null y
      // SIGUE rechazaba la solicitud (01/10/2026). NBAPI ahora pone "Otros medios" por defecto igual.
      conocioInstitucion: (value) => (value ? null : "El campo es obligatorio"),
    },
  });
  const [debouncedValues] = useDebouncedValue(form.values, 300);

  useEffect(() => {
    dispatch(SaveResponses(id, debouncedValues));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    dispatch(GetConocio());
    dispatch(GetPaises());
    dispatch(GetEligio());
    dispatch(GetProvincias());
  }, []);


  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

  const handleSaveResponse = (fieldName, value) => {
    form.setValues({ [fieldName]: value });
  };

  const handleSavePais = (pais) => {
    form.setValues({ ['paisResidencia']: pais });
    form.setValues({ ['provincia']: null });
    form.setValues({ ['provinciaDescripcion']: null });
    if (pais) track('field_completed', { fieldName: 'paisResidencia' });
  };

  const handleSaveResponseProvincia = (provincia) => {
    let prov = dataProvincias.find((p) => p.value === provincia);
    if (prov) {
      form.setValues({ ['provincia']: prov.value });
      form.setValues({ ['provinciaDescripcion']: prov.label });
    } else {
      form.setValues({ ['provincia']: 'OTR' });
      form.setValues({ ['provinciaDescripcion']: provincia });
    }
    if (provincia) track('field_completed', { fieldName: 'provincia' });
  };

  return (
    <Container size="100%">
      <Row className='row-inputs'>
        <Col md={6}>
          <Select
            required
            label="País de Residencia"
            searchable
            data={dataPaises}
            checkIconPosition="right"
            placeholder="Seleccione una opción"
            {...form.getInputProps("paisResidencia", {
              onChange: (value) => {
                handleSavePais(value);
              },
            })}
          />
        </Col>
        <Col md={6}>
          {form.values.paisResidencia === 'AR' ? (
            <Select
              required
              label="Provincia"
              placeholder="Seleccione provincia"
              data={dataProvincias}
              {...form.getInputProps("provincia", {
                onChange: (value) => {
                  handleSaveResponseProvincia(value);
                },
              })}
            />
          ) : (
            <TextInput
              required
              label="Provincia"
              placeholder="Ingrese la provincia"
              {...form.getInputProps("provinciaDescripcion", {
                onChange: (e) => {
                  handleSaveResponseProvincia(e.target.value);
                },
              })}
            />
          )}
        </Col>
        <Col md={6}>
          <TextInput
            required
            label="Localidad"
            placeholder='Vicente López'
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("localidad")}
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            onBlur={trackOnBlur('localidad')}
          />
        </Col>
        <Col md={6}>
          <TextInput
            required
            type="number"
            label="Codigo postal"
            placeholder='1111'
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("codPostal")}
            onBlur={trackOnBlur('codPostal')}
          />
        </Col>
      </Row>
      <Row className='row-inputs'>
        <Col md={3}>
          <TextInput
            required
            label="Calle"
            placeholder='Av.San Juan'
            {...form.getInputProps("calle")}
            onPaste={(e) => e.preventDefault()}
            onKeyDown={(event) => {
              if (!/^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            onBlur={trackOnBlur('calle')}
          />
        </Col>
        <Col md={3}>
          <TextInput
            required
            label="Altura"
            placeholder='123'
            type="number"
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("altura")}
            onBlur={trackOnBlur('altura')}
          />
        </Col>
        <Col md={3}>
          <TextInput
            label="Piso"
            placeholder='01'
            type="number"
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("piso")}
            onBlur={trackOnBlur('piso')}
          />
        </Col>
        <Col md={3}>
          <TextInput
            label="Departamento"
            placeholder='Sí/No'
            onPaste={(e) => e.preventDefault()}
            {...form.getInputProps("departamento")}
            onKeyDown={(event) => {
              if (!/^[a-zA-Z\sáéíóúÁÉÍÓÚñÑ]*$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault();
              }
            }}
            onBlur={trackOnBlur('departamento')}
          />
        </Col>
      </Row>
      <Row className='row-inputs'>
        <Col md={6}>
          <PhoneInputGroup
            label="Teléfono particular"
            placeholder="1122 3344"
            {...form.getInputProps("telefonoParticular")}
            onPaste={(e) => e.preventDefault()}
            countryCode={form.values.codAreaParticular}
            onPhoneChange={(value) => {
              const numericValue = value.replace(/\D/g, "");
              handleSaveResponse("telefonoParticular", numericValue);
              if (/^\d{10,14}$/.test(numericValue)) track('field_completed', { fieldName: 'telefonoParticular' });
            }}
            onCountryChange={(value) => handleSaveResponse("codAreaParticular", value)}
            error={form.errors.telefonoParticular}
            countryOptions={CellPhoneCountrys}
            onKeyDown={(event) => {
              if (!/^\d$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                event.preventDefault(); // Bloquea cualquier tecla que no sea un número
              }
            }}
          />
        </Col>
      </Row>
      <Row className='row-inputs'>
        <Col md={6}>
          <Select
            required
            label="¿Cómo conoció la institución?"
            data={dataConocio?.map((conocio) => {
              const [key, value] = Object.entries(conocio)[0];
              return { value, label: conocio.vc_descripcion };
            })}
            checkIconPosition='right'
            placeholder='Eventos/Charlas'
            {...form.getInputProps("conocioInstitucion")}
            onChange={trackSelect('conocioInstitucion')}
          />
        </Col>
        <Col md={6}>
          <Select
            required
            label="¿Porque eligió la institución?"
            data={dataEligio}
            checkIconPosition='right'
            placeholder='Aranceles accesibles'
            {...form.getInputProps("eligioInstitucion")}
            onChange={trackSelect('eligioInstitucion')}
          />
        </Col>
      </Row>
    </Container>
  );
});

export default ContactDataForm;
